/**
 * /admin — the one screen.
 *
 * A search box and a table, and a panel for one person. That is the whole
 * feature, and keeping it to that is deliberate: two people use this, both of
 * them occasionally, and neither will remember a navigation structure they see
 * once a month. Everything you can do is visible on the row it applies to.
 *
 * The audience constraint from the public site applies here too — 16px minimum
 * body text, 44px targets, real contrast, no colour-only signals — because
 * Caren is one of the two users and an internal tool is not an excuse.
 *
 * Every destructive action goes through a confirm dialog that names the person
 * and spells out the effect in full, rather than asking "Are you sure?". The
 * question is never whether you are sure, it is whether you have understood
 * what happens next, and "Suspend Jane Doe? She will be signed out and lose
 * access to everything until you undo this" is the only version that answers it.
 *
 * The route guard here is cosmetic. The real one is `adminProcedure` on the
 * server: every query below fails closed for anyone who is not an admin, and
 * this page would render an empty table rather than leak anything if the guard
 * were removed. See server/routers/admin.ts.
 */

import { useMemo, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import { Link } from "wouter";
import { Loader2, Pencil, Search, ShieldOff, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { BUTTON } from "@/lib/buttonStyles";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PartnersPanel } from "./AdminPartners";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * The shapes come from the router rather than being restated here.
 *
 * Inferred off AppRouter, so adding a field server-side cannot leave this file
 * quietly reading a property that no longer exists — the build says so.
 */
type AdminOutputs = inferRouterOutputs<
  import("../../../server/routers").AppRouter
>["admin"];
type UserDetail = AdminOutputs["users"]["get"];
type CatalogueProduct = AdminOutputs["products"][number];

const PAGE_SIZE = 25;

/** 12 Mar 2027. Unambiguous in a way that 03/12/2027 is not. */
function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The value an <input type="date"> wants. */
function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const oneYearOut = () => new Date(Date.now() + 365 * 86_400_000);

type AccessBearing = {
  membership: { status: string; currentPeriodEnd: Date | null; comped: boolean } | null;
  products: Array<{ product: string; comped: boolean }>;
};

/**
 * Is their membership live right now?
 *
 * The same two statuses the `entitlements` view accepts, so the tick agrees
 * with what the person can actually open. A cancelled membership that has not
 * reached its period end is still live and still ticks.
 */
function hasLiveMembership(user: AccessBearing): boolean {
  const m = user.membership;
  if (!m) return false;
  return m.status === "active" || m.status === "trialing";
}

/** What the Plus tick says on hover, so the detail is one gesture away. */
function membershipLabel(user: AccessBearing): string {
  const m = user.membership;
  if (!m) return "No membership";
  const comped = m.comped ? " (comped)" : "";
  return hasLiveMembership(user)
    ? `Smart Stack+${comped} to ${formatDate(m.currentPeriodEnd)}`
    : `Smart Stack+${comped} — ${m.status}`;
}

/**
 * "Smart Stack+ to 12 Mar 2027", "Quick Wins (comped)", "none".
 *
 * Reads the underlying rows rather than the entitlements view, so a suspended
 * person still shows what they hold. The view is empty for them, and a column
 * that said "none" would make suspension look like deletion — the one thing it
 * is not. Their status column says they are suspended; this one says what comes
 * back when they are not.
 */
function accessSummary(user: AccessBearing): string {
  const parts: string[] = [];

  if (user.membership) {
    const live =
      user.membership.status === "active" || user.membership.status === "trialing";
    const label = user.membership.comped ? "Smart Stack+ (comped)" : "Smart Stack+";
    parts.push(
      live
        ? `${label} to ${formatDate(user.membership.currentPeriodEnd)}`
        : `${label} — ${user.membership.status}`
    );
  }

  for (const product of user.products) {
    parts.push(product.comped ? `${product.product} (comped)` : product.product);
  }

  return parts.length > 0 ? parts.join(" · ") : "none";
}

/** What to call somebody, given that name and firstName are both optional. */
/**
 * A tick or a dash, with the detail in its accessible name.
 *
 * Replaces a column that printed every entitlement as one long string. With
 * five companion apps it ran past its own cell and overlapped the Status
 * column beside it, so the two most scannable facts about an account — are
 * they a member, do they own anything — were the hardest things on the page
 * to read.
 *
 * Never colour alone: there is a glyph and a text label, and the label is
 * what a screen reader announces. The full list is still one click away
 * under Manage, which is where somebody goes when the answer is "yes, but
 * which".
 */
function AccessMark({ on, label, count }: { on: boolean; label: string; count?: number }) {
  return (
    <span
      title={label}
      className={
        on
          ? "inline-flex items-center gap-1 text-base font-semibold text-foreground"
          : "inline-flex items-center gap-1 text-base text-muted-foreground/60"
      }
    >
      <span aria-hidden="true">{on ? "\u2713" : "\u2013"}</span>
      {on && count && count > 1 ? (
        <span aria-hidden="true" className="text-sm">
          {count}
        </span>
      ) : null}
      <span className="sr-only">{label}</span>
    </span>
  );
}

function displayName(user: { name: string | null; firstName: string | null; email: string | null }) {
  return user.name?.trim() || user.firstName?.trim() || user.email || "this account";
}

// ─── Confirmation ────────────────────────────────────────────────────────────

type Confirmation = {
  title: string;
  body: string;
  actionLabel: string;
  destructive?: boolean;
  /**
   * Ask for a note, which is stored on the audit row.
   *
   * Optional, and only offered where it earns its place. "Why is this person
   * suspended" is the question the history is read to answer, and six months
   * later nobody remembers; "why was Quick Wins comped" is answered well enough
   * by the fact that it was comped.
   */
  reasonLabel?: string;
  run: (reason?: string) => Promise<unknown>;
};

function ConfirmDialog({
  confirmation,
  onClose,
}: {
  confirmation: Confirmation | null;
  onClose: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <AlertDialog
      open={Boolean(confirmation)}
      onOpenChange={open => {
        if (!open) {
          setReason("");
          onClose();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl">{confirmation?.title}</AlertDialogTitle>
          <AlertDialogDescription className="text-base leading-relaxed text-foreground/80">
            {confirmation?.body}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {confirmation?.reasonLabel ? (
          <label className="block text-sm font-medium text-foreground">
            <span className="block mb-1">{confirmation.reasonLabel}</span>
            <Input
              value={reason}
              maxLength={255}
              onChange={event => setReason(event.target.value)}
              placeholder="Optional, but future you will want it"
              className="min-h-11 text-base"
            />
          </label>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={running}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={running}
            className={confirmation?.destructive ? "bg-destructive text-white hover:bg-destructive/90" : ""}
            onClick={async event => {
              // Kept open until the mutation settles, so the result lands while
              // the person is still looking at what they asked for.
              event.preventDefault();
              if (!confirmation) return;
              setRunning(true);
              try {
                await confirmation.run(reason.trim() || undefined);
                setReason("");
                onClose();
              } finally {
                setRunning(false);
              }
            }}
          >
            {running ? "Working…" : confirmation?.actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── The page ────────────────────────────────────────────────────────────────

export default function Admin() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [openUserId, setOpenUserId] = useState<number | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  /* Accounts or Partners. Both live here rather than on two URLs because they
     are the same job — Caren and Daniel open /admin and want either. The
     standalone /admin/partners route still works for a direct link. */
  const [tab, setTab] = useState<"accounts" | "partners">("accounts");

  const utils = trpc.useUtils();

  const listQuery = trpc.admin.users.list.useQuery({
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const detailQuery = trpc.admin.users.get.useQuery(
    { userId: openUserId ?? 0 },
    { enabled: openUserId !== null }
  );

  const productsQuery = trpc.admin.products.useQuery();
  /* Which accounts are Partners, for the badge in the table. One query for
     the whole page rather than one per row, and it is the same cached query
     the panel's "Make them a Partner" reads, so the badge appears the moment
     one is created without a refresh. */
  const partnersQuery = trpc.admin.partners.useQuery();
  const partnerUserIds = useMemo(
    () => new Set((partnersQuery.data ?? []).map(p => p.userId).filter((id): id is number => id !== null)),
    [partnersQuery.data]
  );

  /**
   * After any change, both the list and the open person are refetched.
   *
   * Reading the result back rather than patching the cache by hand. A grant
   * touches the entitlements view, the subscription row and the audit trail at
   * once, and a hand-written optimistic update would have to model all three —
   * getting it subtly wrong is how an admin panel starts lying about state,
   * which is the one thing this tool cannot do.
   */
  const refresh = async () => {
    await Promise.all([utils.admin.users.list.invalidate(), utils.admin.users.get.invalidate()]);
  };

  const onError = (error: { message: string }) => toast.error(error.message);
  const mutationOptions = { onError, onSuccess: refresh };

  const grantMembership = trpc.admin.grantMembership.useMutation(mutationOptions);
  const revokeMembership = trpc.admin.revokeMembership.useMutation(mutationOptions);
  const grantProduct = trpc.admin.grantProduct.useMutation(mutationOptions);
  const revokeProduct = trpc.admin.revokeProduct.useMutation(mutationOptions);
  const suspend = trpc.admin.suspend.useMutation(mutationOptions);
  const unsuspend = trpc.admin.unsuspend.useMutation(mutationOptions);
  const removeUser = trpc.admin.deleteUser.useMutation(mutationOptions);

  const users = listQuery.data?.users ?? [];
  const total = listQuery.data?.total ?? 0;
  const detail = detailQuery.data;

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader context="Admin" showPrimaryAction={false} />

      <main className="flex-1">
        <div className="container max-w-6xl py-8 sm:py-12">
          <div className="flex gap-1 border-b border-border" role="tablist" aria-label="Admin sections">
            {(["accounts", "partners"] as const).map(t => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={
                  "min-h-11 px-4 text-base font-semibold border-b-2 -mb-px transition-colors " +
                  (tab === t
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground")
                }
              >
                {t === "accounts" ? "Accounts" : "Partners"}
              </button>
            ))}
          </div>

          {tab === "partners" ? (
            <PartnersPanel />
          ) : (
          <>
          <p className="mt-8 text-xs font-semibold uppercase tracking-widest text-primary">
            Accounts
          </p>
          <h1 className="mt-3 font-['Fraunces'] text-3xl sm:text-4xl font-semibold text-foreground leading-tight tracking-tight">
            {total === 1 ? "1 account" : `${total} accounts`}
          </h1>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">
            Everything here changes what somebody can reach, straight away. Each
            change is recorded against the account with your name on it.
          </p>

          <form onSubmit={submitSearch} className="mt-6 flex gap-3 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder="Search by name or email"
                aria-label="Search accounts by name or email"
                className="pl-9 min-h-11 text-base"
              />
            </div>
            <button type="submit" className={BUTTON.primarySm}>
              Search
            </button>
            {search ? (
              <button
                type="button"
                className={BUTTON.ghost}
                onClick={() => {
                  setSearchInput("");
                  setSearch("");
                  setPage(0);
                }}
              >
                Clear
              </button>
            ) : null}
          </form>

          <div className="mt-8 rounded-lg border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-sm font-semibold text-foreground">Person</TableHead>
                  <TableHead className="text-sm font-semibold text-foreground">Joined</TableHead>
                  <TableHead className="text-sm font-semibold text-foreground">Last seen</TableHead>
                  <TableHead className="text-sm font-semibold text-foreground text-center">Plus</TableHead>
                  <TableHead className="text-sm font-semibold text-foreground text-center">Apps</TableHead>
                  <TableHead className="text-sm font-semibold text-foreground">Status</TableHead>
                  <TableHead className="text-sm font-semibold text-foreground text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      <Loader2 className="inline w-5 h-5 animate-spin" />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-base text-muted-foreground">
                      {search ? `Nobody matches “${search}”.` : "No accounts yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map(user => (
                    <TableRow key={user.id}>
                      <TableCell className="align-top py-4">
                        <div className="text-base font-semibold text-foreground">
                          {displayName(user)}
                        </div>
                        <div className="text-sm text-muted-foreground break-all">{user.email}</div>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {user.role === "admin" ? (
                            <Badge variant="secondary">Admin</Badge>
                          ) : null}
                          {partnerUserIds.has(user.id) ? (
                            <Badge variant="secondary">Partner</Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="align-top py-4 text-base whitespace-nowrap">
                        {formatDate(user.createdAt)}
                      </TableCell>
                      <TableCell className="align-top py-4 text-base whitespace-nowrap">
                        {formatDate(user.lastSignedIn)}
                      </TableCell>
                      <TableCell className="align-top py-4 text-center">
                        <AccessMark
                          on={hasLiveMembership(user)}
                          label={membershipLabel(user)}
                        />
                      </TableCell>
                      <TableCell className="align-top py-4 text-center">
                        <AccessMark
                          on={user.products.length > 0}
                          label={
                            user.products.length
                              ? `${user.products.length} app${user.products.length === 1 ? "" : "s"}`
                              : "No apps"
                          }
                          count={user.products.length}
                        />
                      </TableCell>
                      <TableCell className="align-top py-4">
                        {user.suspendedAt ? (
                          /* Word and icon, never colour alone. */
                          <span className="inline-flex items-center gap-1.5 text-base font-semibold text-destructive">
                            <ShieldOff className="w-4 h-4" aria-hidden="true" />
                            Suspended
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-base text-foreground/70">
                            <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                            Active
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="align-top py-4 text-right whitespace-nowrap">
                        {/* Icon-only, so it carries a real accessible name
                            that says WHO it edits — "Edit" repeated down a
                            column tells a screen-reader user nothing about
                            which row they are on. 44px square keeps it on the
                            same tap-target floor as everything else. */}
                        <button
                          type="button"
                          onClick={() => setOpenUserId(user.id)}
                          aria-label={`Edit ${displayName(user)}`}
                          title="Edit"
                          className="inline-flex items-center justify-center w-11 h-11 rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                        >
                          <Pencil className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {pageCount > 1 ? (
            <div className="mt-6 flex items-center gap-4">
              <button
                className={BUTTON.secondarySm}
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                Previous
              </button>
              <span className="text-base text-muted-foreground">
                Page {page + 1} of {pageCount}
              </span>
              <button
                className={BUTTON.secondarySm}
                disabled={page + 1 >= pageCount}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          ) : null}

          {/* A dialog rather than a section below the table.
              It used to render underneath, so clicking Manage on row four of
              a long list appeared to do nothing until you scrolled — and the
              account you were editing was off-screen while you edited it. */}
          <Dialog
            open={openUserId !== null}
            onOpenChange={open => {
              if (!open) setOpenUserId(null);
            }}
          >
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
              <DialogTitle className="sr-only">
                {detail ? displayName(detail) : "Account"}
              </DialogTitle>
              {openUserId !== null ? (
            <UserPanel
              detail={detail}
              loading={detailQuery.isLoading}
              products={productsQuery.data ?? []}
              onClose={() => setOpenUserId(null)}
              onConfirm={setConfirmation}
              actions={{
                grantMembership: (userId, currentPeriodEnd) =>
                  grantMembership.mutateAsync({ userId, currentPeriodEnd }),
                revokeMembership: userId => revokeMembership.mutateAsync({ userId }),
                grantProduct: (userId, product) => grantProduct.mutateAsync({ userId, product }),
                revokeProduct: (userId, product) => revokeProduct.mutateAsync({ userId, product }),
                suspend: (userId, reason) => suspend.mutateAsync({ userId, reason }),
                unsuspend: (userId, reason) => unsuspend.mutateAsync({ userId, reason }),
                remove: async (userId, reason) => {
                  await removeUser.mutateAsync({ userId, reason });
                  /* The account this panel is showing no longer exists, so
                     close it rather than leaving a dialog describing a
                     deleted row. */
                  setOpenUserId(null);
                },
              }}
            />
              ) : null}
            </DialogContent>
          </Dialog>
          </>
          )}
        </div>
      </main>

      <ConfirmDialog confirmation={confirmation} onClose={() => setConfirmation(null)} />
      <SiteFooter />
    </div>
  );
}

// ─── One person ──────────────────────────────────────────────────────────────

type PanelActions = {
  grantMembership: (userId: number, currentPeriodEnd: Date) => Promise<unknown>;
  revokeMembership: (userId: number) => Promise<unknown>;
  grantProduct: (userId: number, product: string) => Promise<unknown>;
  revokeProduct: (userId: number, product: string) => Promise<unknown>;
  suspend: (userId: number, reason?: string) => Promise<unknown>;
  unsuspend: (userId: number, reason?: string) => Promise<unknown>;
  remove: (userId: number, reason?: string) => Promise<unknown>;
};

const ACTION_LABELS: Record<string, string> = {
  grant_membership: "Granted Smart Stack+",
  revoke_membership: "Ended Smart Stack+",
  grant_product: "Granted",
  revoke_product: "Revoked",
  suspend: "Suspended the account",
  unsuspend: "Reopened the account",
  delete: "Deleted the account",
};

function UserPanel({
  detail,
  loading,
  products,
  onClose,
  onConfirm,
  actions,
}: {
  detail: UserDetail | undefined;
  loading: boolean;
  products: CatalogueProduct[];
  onClose: () => void;
  onConfirm: (confirmation: Confirmation) => void;
  actions: PanelActions;
}) {
  const [endDate, setEndDate] = useState(() => toDateInputValue(oneYearOut()));

  const held = useMemo(
    () => new Set((detail?.products ?? []).map(p => p.product)),
    [detail]
  );

  if (loading || !detail) {
    return (
      <div className="mt-10 rounded-lg border border-border p-8 text-center">
        <Loader2 className="inline w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const who = displayName(detail);
  const membershipLive =
    detail.membership?.status === "active" || detail.membership?.status === "trialing";

  return (
    <section className="mt-10 rounded-lg border border-border bg-card p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-['Fraunces'] text-2xl font-semibold text-foreground">{who}</h2>
          <p className="text-base text-muted-foreground break-all">{detail.email}</p>
        </div>
        {/* Icon-only, so it needs a real accessible name and a real target.
            44px square keeps it on the same tap-target floor as everything
            else, which an icon button is the easiest place to lose. */}
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${who}`}
          title="Close"
          className="inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      {detail.suspendedAt ? (
        <p className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-base text-foreground">
          <strong className="font-semibold">Suspended {formatDate(detail.suspendedAt)}.</strong>{" "}
          They cannot sign in, and everything below is closed to them until you reopen the
          account. Nothing has been taken away — reopening gives all of it back.
        </p>
      ) : null}

      {/* ── Membership ── */}
      <div className="mt-8 border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground">Smart Stack+</h3>
        <p className="mt-1 text-base text-muted-foreground">
          {detail.membership
            ? `${detail.membership.comped ? "Comped" : "Paid"}, ${detail.membership.status}, ` +
              `${membershipLive ? "runs to" : "ended"} ${formatDate(detail.membership.currentPeriodEnd)}.`
            : "No membership."}
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-sm font-medium text-foreground">
            <span className="block mb-1">Runs until</span>
            <Input
              type="date"
              value={endDate}
              min={toDateInputValue(new Date(Date.now() + 86_400_000))}
              onChange={event => setEndDate(event.target.value)}
              className="min-h-11 text-base w-48"
            />
          </label>
          <button
            className={BUTTON.primarySm}
            onClick={() =>
              onConfirm({
                title: `Give ${who} Smart Stack+?`,
                body:
                  `They get every app in the Smart Stack, including Quick Wins, until ` +
                  `${formatDate(new Date(endDate))}. It is recorded as comped, so it will never ` +
                  `be counted as a sale. On that date it lapses on its own.`,
                actionLabel: "Grant membership",
                run: () => actions.grantMembership(detail.id, new Date(endDate)),
              })
            }
          >
            {detail.membership ? "Extend membership" : "Grant membership"}
          </button>
          {detail.membership && membershipLive ? (
            <button
              className={BUTTON.secondarySm}
              onClick={() =>
                onConfirm({
                  title: `End ${who}'s Smart Stack+?`,
                  body:
                    `They lose every app the membership covers, straight away, unless they have ` +
                    `bought one outright. The record stays; nothing is deleted. You can grant it ` +
                    `again at any time.`,
                  actionLabel: "End membership",
                  destructive: true,
                  run: () => actions.revokeMembership(detail.id),
                })
              }
            >
              End membership
            </button>
          ) : null}
        </div>
      </div>

      {/* ── Individual apps ── */}
      <div className="mt-8 border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground">Apps</h3>
        <ul className="mt-3 space-y-3">
          {products.map(product => {
            const owned = held.has(product.key);
            const entitled = detail.entitledProducts.includes(product.key);
            return (
              <li
                key={product.key}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-4"
              >
                <div>
                  <div className="text-base font-semibold text-foreground">{product.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {owned
                      ? entitled
                        ? "Has it."
                        : "Has it, but cannot reach it while the account is suspended."
                      : entitled
                        ? "Reaches it through the membership."
                        : "No access."}
                  </div>
                </div>
                {owned ? (
                  <button
                    className={BUTTON.secondarySm}
                    onClick={() =>
                      onConfirm({
                        title: `Take ${product.name} away from ${who}?`,
                        body:
                          `They lose it immediately, unless a live Smart Stack+ membership covers ` +
                          `it. The purchase record stays and is marked revoked, so this is visible ` +
                          `later and cannot be undone by a Stripe retry.`,
                        actionLabel: "Revoke app",
                        destructive: true,
                        run: () => actions.revokeProduct(detail.id, product.key),
                      })
                    }
                  >
                    Revoke
                  </button>
                ) : (
                  <button
                    className={BUTTON.secondarySm}
                    onClick={() =>
                      onConfirm({
                        title: `Give ${who} ${product.name}?`,
                        body:
                          `They get it permanently, recorded as comped at zero, so it will never ` +
                          `be counted as a sale. It does not expire.`,
                        actionLabel: "Grant app",
                        run: () => actions.grantProduct(detail.id, product.key),
                      })
                    }
                  >
                    Grant
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── The account itself ── */}
      <div className="mt-8 border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground">Account</h3>
        {detail.role === "admin" ? (
          <p className="mt-2 text-base text-muted-foreground">
            This is an admin account and cannot be suspended from here. Remove the admin role in
            the database first if you really mean to.
          </p>
        ) : detail.suspendedAt ? (
          <button
            className={`${BUTTON.primarySm} mt-3`}
            onClick={() =>
              onConfirm({
                title: `Reopen ${who}'s account?`,
                body:
                  `They can sign in again and get back exactly what they had before — the same ` +
                  `membership, the same apps, nothing extra.`,
                actionLabel: "Reopen account",
                reasonLabel: "Why are you reopening it?",
                run: reason => actions.unsuspend(detail.id, reason),
              })
            }
          >
            Reopen account
          </button>
        ) : (
          <button
            className={`${BUTTON.secondarySm} mt-3`}
            onClick={() =>
              onConfirm({
                title: `Suspend ${who}?`,
                body:
                  `They will be signed out and lose access to everything, including anything they ` +
                  `have paid for, until you undo this. Nothing is deleted or cancelled, and ` +
                  `reopening the account gives all of it back.` +
                  // Suspension is an access decision, not a billing one, and
                  // the two are easy to conflate at the moment you are angry
                  // enough to be doing this.
                  (detail.membership?.comped === false
                    ? ` Their Stripe subscription keeps billing them — cancel it in Stripe too if ` +
                      `they should stop being charged.`
                    : ``),
                actionLabel: "Suspend account",
                destructive: true,
                reasonLabel: "Why are you suspending this account?",
                run: reason => actions.suspend(detail.id, reason),
              })
            }
          >
            Suspend account
          </button>
        )}

        {/* Deleting is the irreversible one, so it is styled as such and sits
            after Suspend rather than beside it — the reversible action should
            be the one your hand lands on. The server refuses outright if the
            account has any payment history; the dialog says so up front so
            that refusal is not a surprise after you have typed a reason. */}
        <button
          className={`${BUTTON.secondarySm} mt-3 border-destructive text-destructive hover:bg-destructive/10`}
          onClick={() =>
            onConfirm({
              title: `Delete ${who}?`,
              body:
                `This cannot be undone. Their account, saved tools and progress are removed ` +
                `for good, and they are signed out everywhere. ` +
                `If they have ever bought anything this will be refused — delete is for test ` +
                `and duplicate accounts; suspend is the one for a real customer.`,
              actionLabel: "Delete permanently",
              destructive: true,
              reasonLabel: "Why are you deleting this account?",
              run: reason => actions.remove(detail.id, reason),
            })
          }
        >
          Delete account
        </button>
      </div>

      {/* ── Make them a Partner ── */}
      <MakePartner detail={detail} />

      {/* ── The audit trail ── */}
      <div className="mt-8 border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground">History</h3>
        <p className="mt-1 text-base text-muted-foreground">
          The last 20 changes. This is what to look at when someone asks why their access changed.
        </p>
        {detail.actions.length === 0 ? (
          <p className="mt-4 text-base text-muted-foreground">Nothing yet.</p>
        ) : (
          <ol className="mt-4 space-y-3">
            {detail.actions.map(entry => (
              <li key={entry.id} className="text-base text-foreground">
                <span className="font-semibold">
                  {ACTION_LABELS[entry.action] ?? entry.action}
                  {entry.detail && entry.action.endsWith("_product") ? ` ${entry.detail}` : ""}
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  — {formatDate(entry.createdAt)} by {entry.actorEmail ?? `account ${entry.actorId}`}
                  {entry.detail && !entry.action.endsWith("_product") ? ` (${entry.detail})` : ""}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <p className="mt-8 text-sm text-muted-foreground">
        <Link href="/my-stack" className="underline hover:text-foreground">
          Back to the site
        </Link>
      </p>
    </section>
  );
}

/**
 * Turn this account into a Partner, from the panel you are already in.
 *
 * The alternative was retyping their name, email and user id into the form on
 * the Partners tab, which is both tedious and the kind of copying that gets a
 * digit wrong — and a Partner row linked to the wrong userId shows one person
 * another person's referrals.
 *
 * The code is SUGGESTED, not imposed. It is derived from their name, and the
 * suggestion is normalised by the same rule the tracking cookie uses, so
 * whatever is submitted is a code that will actually track. If it is taken the
 * server says so and nothing is created.
 */
function MakePartner({ detail }: { detail: UserDetail }) {
  const partners = trpc.admin.partners.useQuery();
  const create = trpc.admin.createPartner.useMutation();
  const utils = trpc.useUtils();

  const existing = (partners.data ?? []).find(p => p.userId === detail.id);

  /* Their name, lowercased, non-letters collapsed to hyphens. Falls back to
     the local part of their email when the name gives nothing usable. */
  const suggested = (() => {
    const source = (detail.name || detail.firstName || detail.email || "").split("@")[0];
    const slug = source
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32);
    return slug.length >= 2 ? slug : "";
  })();

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(suggested);
  const [error, setError] = useState("");

  if (partners.isLoading) return null;

  if (existing) {
    return (
      <div className="mt-8 border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground">Partner</h3>
        <p className="mt-1 text-base text-muted-foreground">
          Already a Partner, code{" "}
          <span className="font-mono text-foreground">{existing.code}</span> at{" "}
          {(existing.commissionBps / 100).toFixed(0)}%. Their figures are on the Partners tab.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 border-t border-border pt-6">
      <h3 className="text-lg font-semibold text-foreground">Partner</h3>
      {!open ? (
        <>
          <p className="mt-1 text-base text-muted-foreground">
            Not a Partner. Making them one links this account, so they can sign in and see
            their own referrals and commissions.
          </p>
          <button type="button" className={`${BUTTON.secondarySm} mt-4`} onClick={() => setOpen(true)}>
            Make them a Partner
          </button>
        </>
      ) : (
        <div className="mt-3 grid gap-3 max-w-md">
          <label className="grid gap-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Their code
            </span>
            <input
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={32}
              className="w-full rounded-md border border-border bg-background px-3 py-3 font-mono"
            />
            <span className="text-sm text-muted-foreground">
              Goes in their links. 40% and no second tier to start &mdash; change either on
              the Partners tab.
            </span>
          </label>

          {error && <p className="text-base font-semibold text-destructive">{error}</p>}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={create.isPending}
              className={BUTTON.primarySm}
              onClick={async () => {
                setError("");
                try {
                  await create.mutateAsync({
                    code,
                    name: displayName(detail),
                    email: detail.email ?? "",
                    userId: detail.id,
                    commissionBps: 4000,
                    tier2Bps: 0,
                    parentPartnerId: null,
                    status: "active",
                    notes: null,
                  });
                  await utils.admin.partners.invalidate();
                  setOpen(false);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "That did not work.");
                }
              }}
            >
              {create.isPending ? "Creating…" : "Create"}
            </button>
            <button type="button" className={BUTTON.ghost} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
